import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { CalendarClock24Regular } from '@fluentui/react-icons';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideSegmented } from '../../../theme/components/GuideSegmented';
import { StatusPill } from '../../../theme/components/StatusPill';
import { useContracts } from '../Dashboard.Hooks';
import { ExpiringContractsSettings, ExpiringContractsWindow, WidgetProps } from '../Dashboard.Types';
import { WidgetData } from './WidgetData';
import { expiringContracts, expiringTone } from './Widgets.Functions';

const WINDOWS: ExpiringContractsWindow[] = [30, 60, 90];

export const windowOf = (settings: WidgetProps['widget']['settings']): ExpiringContractsWindow => {
  const days = (settings as Partial<ExpiringContractsSettings> | undefined)?.days;
  return WINDOWS.includes(days as ExpiringContractsWindow) ? (days as ExpiringContractsWindow) : 60;
};

/** Contratos activos cuya vigencia termina dentro de la ventana elegida (30 / 60 / 90 días). */
export const ExpiringContractsWidget = ({ widget, onSettingsChange }: WidgetProps) => {
  const { t } = useTranslation();
  const contracts = useContracts();
  const days = windowOf(widget.settings);
  const items = React.useMemo(() => expiringContracts(contracts.list, days), [contracts.list, days]);

  const daysLabel = (daysLeft: number): string => {
    if (daysLeft < 0) return t('dashboard.widgets.expiringContracts.expired', { count: -daysLeft });
    if (daysLeft === 0) return t('dashboard.widgets.expiringContracts.today');
    return t('dashboard.widgets.expiringContracts.daysLeft', { count: daysLeft });
  };

  return (
    <WidgetData loading={contracts.loading} failed={contracts.failed} error={contracts.error} onRetry={contracts.reload}>
      <div className="dbw-toolbar">
        <GuideSegmented<string>
          label={t('dashboard.widgets.expiringContracts.window')}
          ariaLabel={t('dashboard.widgets.expiringContracts.window')}
          options={WINDOWS.map((value) => ({ value: String(value), label: t('dashboard.widgets.expiringContracts.days', { count: value }) }))}
          value={String(days)}
          onChange={(value) => onSettingsChange({ days: Number(value) })}
          data-testid="expiring-contracts-window"
        />
      </div>
      {items.length === 0 ? (
        <GuideEmptyState icon={<CalendarClock24Regular />} title={t('dashboard.widgets.expiringContracts.empty', { days })} data-testid="expiring-contracts-empty" />
      ) : (
        <ul className="dbw-list" data-testid="expiring-contracts-list">
          {items.map(({ contract, daysLeft }) => (
            <li key={contract.id} className="dbw-list__row">
              <div className="dbw-list__main">
                <Link to={`/contracts/${contract.id}`} className="dbw-list__title" data-testid={`expiring-contract-${contract.id}`}>
                  {contract.contractNumber}
                  {contract.contractDescription ? ` · ${contract.contractDescription}` : ''}
                </Link>
                <span className="dbw-list__meta">{t('dashboard.widgets.expiringContracts.ends', { date: moment(contract.contractEndDate).format('DD/MM/YYYY') })}</span>
              </div>
              <div className="dbw-list__side">
                <StatusPill tone={expiringTone(daysLeft)} size="small" data-testid={`expiring-contract-days-${contract.id}`}>
                  {daysLabel(daysLeft)}
                </StatusPill>
              </div>
            </li>
          ))}
        </ul>
      )}
    </WidgetData>
  );
};

export default ExpiringContractsWidget;
